import { computed, inject } from '@angular/core';
import { ApiService } from '@libs/core/base-config';
import {
  AsyncState,
  asResult,
  computedFailure,
  dataFromState,
  initialState,
  loadingState,
  successState,
} from '@libs/core/lib/state/async-states';
import {
  adminProductsDecision,
  adminProductsList,
  getProductById,
  landingPage,
  productCreate,
  productDelete,
  productUpdate,
  productsList,
  productsListByFilter,
  relatedProductsList,
  requestToPublishProduct,
  unPublishProduct,
  vendorGetProductById,
  wishList,
} from '@libs/mtjr/shared/generated/entities/product/service/product.service';
import { Dtos, Entity } from '@libs/mtjr/shared/generated/types';
import { patchState, signalStoreFeature, withComputed, withMethods, withState } from '@ngrx/signals';

export function withProductsList() {
  type ProductsListState = {
    productsListState: AsyncState<Dtos.ProductsListResponse>;
    productsListInput: Dtos.ProductsListQueryParams;
    productsListCanFetchNext: boolean;
    productsListFetchingNext: boolean;
  };
  return signalStoreFeature(
    withState<ProductsListState>({
      productsListState: initialState(),
      productsListInput: { page: 1, totalPerPage: 20 },
      productsListCanFetchNext: false,
      productsListFetchingNext: false,
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async productsListPage(input?: Partial<Dtos.ProductsListQueryParams>) {
        const mergedInput = { ...store.productsListInput(), ...input };
        patchState(store, {
          productsListState: mergedInput.page === 1 ? loadingState() : store.productsListState(),
          productsListInput: mergedInput,
          productsListFetchingNext: mergedInput.page > 1,
        });
        const result = await asResult(productsList(apiService, store.productsListInput()));
        if (result.success) {
          const cState = store.productsListState();
          let products = result.data.products;
          if (cState.success && mergedInput.page > 1) {
            products = [...cState.data.products, ...products];
          }
          patchState(store, {
            productsListState: successState({ ...result.data, products: products }),
            productsListCanFetchNext: result.data.products.length === mergedInput.totalPerPage,
            productsListFetchingNext: false,
          });
        } else {
          patchState(store, {
            productsListState: result,
            productsListCanFetchNext: false,
            productsListFetchingNext: false,
          });
        }
      },

      async productsListFetchNextPage() {
        if (store.productsListCanFetchNext() === false) return;
        const input = store.productsListInput();
        await this.productsListPage({ ...input, page: input.page + 1 });
      },
    })),

    withComputed((store) => ({
      products: dataFromState(store.productsListState, (data) => data?.products ?? []),
      productsFailure: computedFailure(store.productsListState),
    })),
  );
}

export function withRelatedProductsList() {
  type RelatedProductsListState = {
    relatedProductsListState: AsyncState<Dtos.RelatedProductsListResponse>;
    relatedProductsListInput: Dtos.RelatedProductsListQueryParams;
    relatedProductsListCanFetchNext: boolean;
    relatedProductsListFetchingNext: boolean;
  };
  return signalStoreFeature(
    withState<RelatedProductsListState>({
      relatedProductsListState: initialState(),
      relatedProductsListInput: { page: 1, totalPerPage: 20 },
      relatedProductsListCanFetchNext: false,
      relatedProductsListFetchingNext: false,
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async relatedProductsListPage(
        { productId }: Dtos.RelatedProductsListPathParams,
        input?: Partial<Dtos.RelatedProductsListQueryParams>,
      ) {
        const mergedInput = { ...store.relatedProductsListInput(), ...input };
        patchState(store, {
          relatedProductsListState: mergedInput.page === 1 ? loadingState() : store.relatedProductsListState(),
          relatedProductsListInput: mergedInput,
          relatedProductsListFetchingNext: mergedInput.page > 1,
        });
        const result = await asResult(relatedProductsList(apiService, store.relatedProductsListInput(), { productId }));
        if (result.success) {
          const cState = store.relatedProductsListState();
          let relatedProducts = result.data.relatedProducts;
          if (cState.success && mergedInput.page > 1) {
            relatedProducts = [...cState.data.relatedProducts, ...relatedProducts];
          }
          patchState(store, {
            relatedProductsListState: successState({ ...result.data, relatedProducts: relatedProducts }),
            relatedProductsListCanFetchNext: result.data.relatedProducts.length === mergedInput.totalPerPage,
            relatedProductsListFetchingNext: false,
          });
        } else {
          patchState(store, {
            relatedProductsListState: result,
            relatedProductsListCanFetchNext: false,
            relatedProductsListFetchingNext: false,
          });
        }
      },

      async relatedProductsListFetchNextPage({ productId }: Dtos.RelatedProductsListPathParams) {
        if (store.relatedProductsListCanFetchNext() === false) return;
        const input = store.relatedProductsListInput();
        await this.relatedProductsListPage({ productId }, { ...input, page: input.page + 1 });
      },
    })),

    withComputed((store) => ({
      relatedProducts: dataFromState(store.relatedProductsListState, (data) => data?.relatedProducts ?? []),
      relatedProductsFailure: computedFailure(store.relatedProductsListState),
    })),
  );
}

export function withAdminProductsList() {
  type AdminProductsListState = {
    adminProductsListState: AsyncState<Dtos.AdminProductsListResponse>;
    adminProductsListInput: Dtos.AdminProductsListQueryParams;
    adminProductsListCanFetchNext: boolean;
    adminProductsListFetchingNext: boolean;
  };
  return signalStoreFeature(
    withState<AdminProductsListState>({
      adminProductsListState: initialState(),
      adminProductsListInput: { page: 1, totalPerPage: 20 },
      adminProductsListCanFetchNext: false,
      adminProductsListFetchingNext: false,
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async adminProductsListPage(input?: Partial<Dtos.AdminProductsListQueryParams>) {
        const mergedInput = { ...store.adminProductsListInput(), ...input };
        patchState(store, {
          adminProductsListState: mergedInput.page === 1 ? loadingState() : store.adminProductsListState(),
          adminProductsListInput: mergedInput,
          adminProductsListFetchingNext: mergedInput.page > 1,
        });
        const result = await asResult(adminProductsList(apiService, store.adminProductsListInput()));
        if (result.success) {
          const cState = store.adminProductsListState();
          let products = result.data.products;
          if (cState.success && mergedInput.page > 1) {
            products = [...cState.data.products, ...products];
          }
          patchState(store, {
            adminProductsListState: successState({ ...result.data, products: products }),
            adminProductsListCanFetchNext: result.data.products.length === mergedInput.totalPerPage,
            adminProductsListFetchingNext: false,
          });
        } else {
          patchState(store, {
            adminProductsListState: result,
            adminProductsListCanFetchNext: false,
            adminProductsListFetchingNext: false,
          });
        }
      },

      async adminProductsListFetchNextPage() {
        if (store.adminProductsListCanFetchNext() === false) return;
        const input = store.adminProductsListInput();
        await this.adminProductsListPage({ ...input, page: input.page + 1 });
      },
    })),

    withComputed((store) => ({
      products: dataFromState(store.adminProductsListState, (data) => data?.products ?? []),
      productsFailure: computedFailure(store.adminProductsListState),
    })),
  );
}

export function withWishList() {
  type WishListState = {
    wishListState: AsyncState<Dtos.WishListResponse>;
    wishListInput: Dtos.WishListQueryParams;
    wishListCanFetchNext: boolean;
    wishListFetchingNext: boolean;
  };
  return signalStoreFeature(
    withState<WishListState>({
      wishListState: initialState(),
      wishListInput: { page: 1, totalPerPage: 20 },
      wishListCanFetchNext: false,
      wishListFetchingNext: false,
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async wishListPage(input?: Partial<Dtos.WishListQueryParams>) {
        const mergedInput = { ...store.wishListInput(), ...input };
        patchState(store, {
          wishListState: mergedInput.page === 1 ? loadingState() : store.wishListState(),
          wishListInput: mergedInput,
          wishListFetchingNext: mergedInput.page > 1,
        });
        const result = await asResult(wishList(apiService, store.wishListInput()));
        if (result.success) {
          const cState = store.wishListState();
          let products = result.data.products;
          if (cState.success && mergedInput.page > 1) {
            products = [...cState.data.products, ...products];
          }
          patchState(store, {
            wishListState: successState({ ...result.data, products: products }),
            wishListCanFetchNext: result.data.products.length === mergedInput.totalPerPage,
            wishListFetchingNext: false,
          });
        } else {
          patchState(store, {
            wishListState: result,
            wishListCanFetchNext: false,
            wishListFetchingNext: false,
          });
        }
      },

      async wishListFetchNextPage() {
        if (store.wishListCanFetchNext() === false) return;
        const input = store.wishListInput();
        await this.wishListPage({ ...input, page: input.page + 1 });
      },
    })),

    withComputed((store) => ({
      products: dataFromState(store.wishListState, (data) => data?.products ?? []),
      productsFailure: computedFailure(store.wishListState),
    })),
  );
}

export function withProductsListByFilter() {
  type ProductsListByFilterState = {
    productsListByFilterState: AsyncState<Dtos.ProductsListByFilterResponse>;
    productsListByFilterInput: Dtos.ProductsListByFilterQueryParams;
    productsListByFilterCanFetchNext: boolean;
    productsListByFilterFetchingNext: boolean;
  };
  return signalStoreFeature(
    withState<ProductsListByFilterState>({
      productsListByFilterState: initialState(),
      productsListByFilterInput: { page: 1, totalPerPage: 20 },
      productsListByFilterCanFetchNext: false,
      productsListByFilterFetchingNext: false,
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async productsListByFilterPage(input?: Partial<Dtos.ProductsListByFilterQueryParams>) {
        const mergedInput = { ...store.productsListByFilterInput(), ...input };
        patchState(store, {
          productsListByFilterState: mergedInput.page === 1 ? loadingState() : store.productsListByFilterState(),
          productsListByFilterInput: mergedInput,
          productsListByFilterFetchingNext: mergedInput.page > 1,
        });
        const result = await asResult(productsListByFilter(apiService, store.productsListByFilterInput()));
        if (result.success) {
          const cState = store.productsListByFilterState();
          let products = result.data.products;
          if (cState.success && mergedInput.page > 1) {
            products = [...cState.data.products, ...products];
          }
          patchState(store, {
            productsListByFilterState: successState({ ...result.data, products: products }),
            productsListByFilterCanFetchNext: result.data.products.length === mergedInput.totalPerPage,
            productsListByFilterFetchingNext: false,
          });
        } else {
          patchState(store, {
            productsListByFilterState: result,
            productsListByFilterCanFetchNext: false,
            productsListByFilterFetchingNext: false,
          });
        }
      },

      async productsListByFilterFetchNextPage() {
        if (store.productsListByFilterCanFetchNext() === false) return;
        const input = store.productsListByFilterInput();
        await this.productsListByFilterPage({ ...input, page: input.page + 1 });
      },
    })),

    withComputed((store) => ({
      products: dataFromState(store.productsListByFilterState, (data) => data?.products ?? []),
      productsFailure: computedFailure(store.productsListByFilterState),
    })),
  );
}

export function withRequestToPublishProduct() {
  return signalStoreFeature(
    withState<{ requestToPublishProductState: AsyncState<void> }>({
      requestToPublishProductState: initialState(),
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async requestToPublishProduct({ productId }: Dtos.RequestToPublishProductPathParams) {
        patchState(store, { requestToPublishProductState: loadingState() });
        const result = await asResult(requestToPublishProduct(apiService, { productId }));
        patchState(store, { requestToPublishProductState: result });
      },
    })),
    withComputed((store) => ({
      requestToPublishProductLoading: computed(() => store.requestToPublishProductState().type === 'loading'),
      requestToPublishProductFailure: computedFailure(store.requestToPublishProductState),
    })),
  );
}

export function withUnPublishProduct() {
  return signalStoreFeature(
    withState<{ unPublishProductState: AsyncState<void> }>({
      unPublishProductState: initialState(),
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async unPublishProduct({ productId }: Dtos.UnPublishProductPathParams) {
        patchState(store, { unPublishProductState: loadingState() });
        const result = await asResult(unPublishProduct(apiService, { productId }));
        patchState(store, { unPublishProductState: result });
      },
    })),
    withComputed((store) => ({
      unPublishProductLoading: computed(() => store.unPublishProductState().type === 'loading'),
      unPublishProductFailure: computedFailure(store.unPublishProductState),
    })),
  );
}

export function withAdminProductsDecision() {
  return signalStoreFeature(
    withState<{ adminProductsDecisionState: AsyncState<void> }>({
      adminProductsDecisionState: initialState(),
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async adminProductsDecision(adminProductsDecisionList: Dtos.AdminProductsDecisionList) {
        patchState(store, { adminProductsDecisionState: loadingState() });
        const result = await asResult(adminProductsDecision(apiService, adminProductsDecisionList));
        patchState(store, { adminProductsDecisionState: result });
      },
    })),
    withComputed((store) => ({
      adminProductsDecisionLoading: computed(() => store.adminProductsDecisionState().type === 'loading'),
      adminProductsDecisionFailure: computedFailure(store.adminProductsDecisionState),
    })),
  );
}

export function withProductDelete() {
  return signalStoreFeature(
    withState<{ productDeleteState: AsyncState<void> }>({
      productDeleteState: initialState(),
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async productDelete({ productId }: Dtos.ProductDeletePathParams) {
        patchState(store, { productDeleteState: loadingState() });
        const result = await asResult(productDelete(apiService, { productId }));
        patchState(store, { productDeleteState: result });
      },
    })),
    withComputed((store) => ({
      productDeleteLoading: computed(() => store.productDeleteState().type === 'loading'),
      productDeleteFailure: computedFailure(store.productDeleteState),
    })),
  );
}

export function withProductUpdate() {
  return signalStoreFeature(
    withState<{ productUpdateState: AsyncState<void> }>({
      productUpdateState: initialState(),
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async productUpdate(productUpdateBody: Dtos.ProductUpdateBody, { productId }: Dtos.ProductUpdatePathParams) {
        patchState(store, { productUpdateState: loadingState() });
        const result = await asResult(productUpdate(apiService, productUpdateBody, { productId }));
        patchState(store, { productUpdateState: result });
      },
    })),
    withComputed((store) => ({
      productUpdateLoading: computed(() => store.productUpdateState().type === 'loading'),
      productUpdateFailure: computedFailure(store.productUpdateState),
    })),
  );
}

export function withProductCreate() {
  return signalStoreFeature(
    withState<{ productCreateState: AsyncState<Entity.Product> }>({
      productCreateState: initialState(),
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async productCreate(productCreateBody: Dtos.ProductCreateBody, { vendorId }: Dtos.ProductCreatePathParams) {
        patchState(store, { productCreateState: loadingState() });
        const result = await asResult(productCreate(apiService, productCreateBody, { vendorId }));
        patchState(store, { productCreateState: result });
      },
    })),
    withComputed((store) => ({
      productCreateLoading: computed(() => store.productCreateState().type === 'loading'),
      productCreateFailure: computedFailure(store.productCreateState),
    })),
  );
}

export function withVendorGetProductById() {
  return signalStoreFeature(
    withState<{ vendorGetProductByIdState: AsyncState<Dtos.Product> }>({
      vendorGetProductByIdState: initialState(),
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async vendorGetProductById({ id }: Dtos.VendorGetProductByIdPathParams) {
        patchState(store, { vendorGetProductByIdState: loadingState() });
        const result = await asResult(vendorGetProductById(apiService, { id }));
        patchState(store, { vendorGetProductByIdState: result });
      },
    })),
    withComputed((store) => ({
      vendorGetProductByIdLoading: computed(() => store.vendorGetProductByIdState().type === 'loading'),
      vendorGetProductByIdFailure: computedFailure(store.vendorGetProductByIdState),
    })),
  );
}

export function withGetProductById() {
  return signalStoreFeature(
    withState<{ getProductByIdState: AsyncState<Dtos.Product> }>({
      getProductByIdState: initialState(),
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async getProductById({ id }: Dtos.GetProductByIdPathParams) {
        patchState(store, { getProductByIdState: loadingState() });
        const result = await asResult(getProductById(apiService, { id }));
        patchState(store, { getProductByIdState: result });
      },
    })),
    withComputed((store) => ({
      getProductByIdLoading: computed(() => store.getProductByIdState().type === 'loading'),
      getProductByIdFailure: computedFailure(store.getProductByIdState),
    })),
  );
}

export function withLandingPage() {
  return signalStoreFeature(
    withState<{ landingPageState: AsyncState<Dtos.LandingPageResponse> }>({
      landingPageState: initialState(),
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async landingPage() {
        patchState(store, { landingPageState: loadingState() });
        const result = await asResult(landingPage(apiService));
        patchState(store, { landingPageState: result });
      },
    })),
    withComputed((store) => ({
      landingPageLoading: computed(() => store.landingPageState().type === 'loading'),
      landingPageFailure: computedFailure(store.landingPageState),
    })),
  );
}
